'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { ArrowRight, Check, Download, FileCheck2, Loader2, Sparkles, Building2 } from 'lucide-react'
import { useAuth } from '@/lib/auth/context'
import { useAuthModal } from '@/components/auth/auth-modal-context'
import { AvulsoComprovanteButton } from '@/components/billing/avulso-comprovante-button'
import { AVULSO_COMPROVANTE, PLANS } from '@/lib/billing/plans'
import { trackCheckoutIniciado } from '@/lib/analytics'

/** localStorage: guarda os 12 meses durante o round-trip do Mercado Pago (guest). */
const GUEST_STORE_KEY = 'cm_guest_comprovante'

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function parseNum(s: string): number {
  const n = parseFloat(
    s
      .replace(/\./g, '')
      .replace(',', '.')
      .replace(/[^\d.]/g, ''),
  )
  return Number.isNaN(n) ? 0 : n
}

const fmt = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/** Mini-mockup de um comprovante — com ou sem marca d'água — pra comparação visual. */
function MiniDoc({ watermark, label, tone }: { watermark?: boolean; label: string; tone: 'free' | 'premium' }) {
  return (
    <div className="flex-1">
      <div className="relative overflow-hidden rounded-md border border-line bg-white p-2.5 shadow-sm">
        <div className="h-1.5 w-2/3 rounded bg-ink/70" />
        <div className="mt-1 h-1 w-1/3 rounded bg-ink/20" />
        <div className="mt-2.5 space-y-1">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex justify-between gap-2">
              <div className="h-1 w-1/2 rounded bg-ink/15" />
              <div className="h-1 w-1/5 rounded bg-ink/25" />
            </div>
          ))}
        </div>
        <div className="mt-2.5 h-1.5 w-2/5 rounded bg-brand/60" />
        {watermark && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="rotate-[-22deg] text-[9px] font-extrabold tracking-wider text-ink/20">
              CALCULAMEI · GRÁTIS
            </span>
          </div>
        )}
      </div>
      <p
        className={
          tone === 'premium'
            ? 'mt-1.5 text-center text-[11px] font-bold text-brand'
            : 'mt-1.5 text-center text-[11px] font-medium text-ink-faint'
        }
      >
        {label}
      </p>
    </div>
  )
}

export function ComprovanteGenerator() {
  const { user, refreshUser } = useAuth()
  const { openSignup } = useAuthModal()
  const searchParams = useSearchParams()
  const wantClean = searchParams.get('limpo') === '1'
  // Volta do checkout guest: ?guest=<token>&payment_id=<id>&status=approved
  const guestToken = searchParams.get('guest')
  const guestPaymentId = searchParams.get('payment_id')
  const guestStatus = searchParams.get('status')

  const isPremium = user?.plano === 'PREMIUM' || user?.role === 'ADMIN'
  const credits = user?.comprovanteCredits ?? 0
  const hasCleanAccess = isPremium || credits > 0

  const [nome, setNome] = useState('')
  const [cnpj, setCnpj] = useState('')
  const [tipoMEI, setTipoMEI] = useState('')
  const [valores, setValores] = useState<string[]>(() => Array(12).fill(''))
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [erro, setErro] = useState('')
  const [autoState, setAutoState] = useState<'idle' | 'loading' | 'error'>('idle')
  const [autoErro, setAutoErro] = useState('')
  const [guestLoading, setGuestLoading] = useState(false)
  /** Setado na volta do MP: token + id do pagamento aprovado (libera o limpo). */
  const [guestPaid, setGuestPaid] = useState<{ token: string; paymentId: string } | null>(null)

  // Após pagamento (?limpo=1), refresca a conta pra pegar o crédito.
  useEffect(() => {
    if (wantClean) refreshUser()
  }, [wantClean, refreshUser])

  // Volta do checkout guest: restaura os 12 meses que salvamos antes do redirect
  // e, se o pagamento veio aprovado, libera o botão de baixar limpo.
  useEffect(() => {
    if (!guestToken) return
    try {
      const raw = localStorage.getItem(GUEST_STORE_KEY)
      if (raw) {
        const saved = JSON.parse(raw)
        if (saved?.token === guestToken) {
          if (saved.nome) setNome(saved.nome)
          if (saved.cnpj) setCnpj(saved.cnpj)
          if (saved.tipoMEI) setTipoMEI(saved.tipoMEI)
          if (Array.isArray(saved.valores) && saved.valores.length === 12) setValores(saved.valores)
        }
      }
    } catch {
      /* localStorage indisponível */
    }
    if (guestPaymentId && guestStatus === 'approved') {
      setGuestPaid({ token: guestToken, paymentId: guestPaymentId })
    }
  }, [guestToken, guestPaymentId, guestStatus])

  /** Inicia a compra do limpo SEM cadastro: salva os meses e vai pro Mercado Pago. */
  async function comprarLimpoGuest() {
    setErro('')
    if (!nome.trim()) {
      setErro('Informe seu nome antes de comprar.')
      return
    }
    if (total <= 0) {
      setErro('Preencha o faturamento de pelo menos um mês antes de comprar.')
      return
    }
    setGuestLoading(true)
    try {
      const res = await fetch('/api/billing/checkout-avulso-guest', { method: 'POST' })
      const data = await res.json().catch(() => null)
      if (!data?.url || !data?.token) {
        setErro('Não deu pra iniciar o pagamento agora. Tente de novo.')
        setGuestLoading(false)
        return
      }
      try {
        localStorage.setItem(
          GUEST_STORE_KEY,
          JSON.stringify({ token: data.token, nome: nome.trim(), cnpj: cnpj.trim(), tipoMEI, valores }),
        )
      } catch {
        /* segue sem salvar; a volta pedirá pra preencher de novo */
      }
      trackCheckoutIniciado('comprovante_avulso')
      window.location.href = data.url
    } catch {
      setErro('Erro de conexão. Tente de novo.')
      setGuestLoading(false)
    }
  }

  async function gerarAuto() {
    setAutoErro('')
    setAutoState('loading')
    try {
      const r = await fetch('/api/comprovante')
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        setAutoErro(d.message || 'Não deu pra gerar automático agora.')
        setAutoState('error')
        return
      }
      const blob = await r.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'comprovante-faturamento-mei.pdf'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setAutoState('idle')
      // Atualiza créditos restantes na UI
      await refreshUser()
    } catch {
      setAutoErro('Não deu pra gerar agora. Tenta de novo.')
      setAutoState('error')
    }
  }

  const janela = useMemo(() => {
    const now = new Date()
    const arr: { mes: number; ano: number; label: string }[] = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      arr.push({ mes: d.getMonth() + 1, ano: d.getFullYear(), label: `${MESES[d.getMonth()]}/${d.getFullYear()}` })
    }
    return arr
  }, [])

  const total = valores.reduce((s, v) => s + parseNum(v), 0)

  function setValor(i: number, v: string) {
    setValores((prev) => {
      const next = [...prev]
      next[i] = v
      return next
    })
  }

  async function gerar(opts?: { guestToken?: string; paymentId?: string }) {
    setErro('')
    if (!nome.trim()) {
      setErro('Informe seu nome.')
      return
    }
    if (total <= 0) {
      setErro('Preencha o faturamento de pelo menos um mês.')
      return
    }
    setState('loading')
    try {
      const meses = janela.map((j, i) => ({ mes: j.mes, ano: j.ano, valor: parseNum(valores[i]) }))
      const r = await fetch('/api/comprovante-publico', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: nome.trim(),
          cnpj: cnpj.trim() || undefined,
          tipoMEI: tipoMEI || undefined,
          meses,
          // guest pago: libera o limpo (o servidor confere no MP)
          guestToken: opts?.guestToken,
          paymentId: opts?.paymentId,
        }),
      })
      if (!r.ok) throw new Error('falhou')
      const blob = await r.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'comprovante-faturamento-mei.pdf'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setState('done')
      await refreshUser()
    } catch {
      setState('error')
      setErro('Não deu pra gerar agora. Tenta de novo.')
    }
  }

  const labelCls = 'text-[13px] font-semibold text-ink-mute'
  const inputCls =
    'mt-1 h-11 w-full rounded-lg border border-line bg-surface px-3 text-[14px] text-ink outline-none focus:border-brand'

  // Botão de comprar o limpo SEM cadastro (guest). Anônimo usa este caminho;
  // logado usa o AvulsoComprovanteButton (credita a conta pelo fluxo antigo).
  const guestBuyBtn = (
    <button
      type="button"
      onClick={comprarLimpoGuest}
      disabled={guestLoading}
      className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-[13.5px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
    >
      {guestLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck2 className="h-4 w-4" />}
      Baixar limpo — {AVULSO_COMPROVANTE.priceDisplay}
    </button>
  )

  return (
    <div className="border border-line bg-surface p-5 sm:p-6">
      {/* Volta do checkout guest com pagamento aprovado — caminho direto pro limpo */}
      {guestPaid && (
        <div className="mb-6 border-2 border-brand bg-brand-tint px-5 py-4">
          <p className="flex items-center gap-2 text-[14px] font-bold text-ink">
            <Check className="h-4 w-4 text-brand" /> Pagamento aprovado!
          </p>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-soft">
            Restauramos os 12 meses que você preencheu (confira abaixo). Clique pra baixar seu
            comprovante <strong>limpo, sem marca</strong>.
          </p>
          <button
            type="button"
            onClick={() => gerar({ guestToken: guestPaid.token, paymentId: guestPaid.paymentId })}
            disabled={state === 'loading'}
            className="mt-3 inline-flex h-11 items-center justify-center gap-2 bg-brand px-5 text-[14px] font-bold text-white hover:bg-brand-dark disabled:opacity-60"
          >
            {state === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Baixar comprovante limpo
          </button>
          {erro && <p className="mt-2 text-[13px] text-red-600">{erro}</p>}
        </div>
      )}

      {/* Guest voltou com pagamento em processamento */}
      {guestToken && !guestPaid && guestStatus === 'pending' && (
        <div className="mb-6 border border-amber-line bg-amber-bg px-5 py-4">
          <p className="text-[14px] font-bold text-amber">Pagamento em processamento</p>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-soft">
            Assim que o Mercado Pago confirmar, recarregue esta página pra baixar o comprovante limpo.
          </p>
        </div>
      )}

      {/* Quem já pagou / Premium — caminho direto, sem enrolação */}
      {user && hasCleanAccess && (
        <div
          id="gerar-limpo"
          className="mb-6 border-2 border-brand bg-brand-tint px-5 py-4"
        >
          <p className="text-[14px] font-bold text-ink">
            {isPremium
              ? 'Premium ativo — gere o comprovante limpo agora'
              : `Você tem ${credits} crédito${credits === 1 ? '' : 's'} de comprovante limpo`}
          </p>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-soft">
            {isPremium
              ? 'Puxamos os 12 meses do painel e geramos o PDF sem marca.'
              : 'Clique abaixo: puxamos os 12 meses do painel e o PDF sai sem marca. Se ainda não lançou o faturamento, preencha os meses manualmente mais abaixo.'}
          </p>
          <button
            type="button"
            onClick={gerarAuto}
            disabled={autoState === 'loading'}
            className="mt-3 inline-flex h-11 items-center justify-center gap-2 bg-brand px-5 text-[14px] font-bold text-white hover:bg-brand-dark disabled:opacity-60"
          >
            {autoState === 'loading' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Gerar PDF limpo agora
          </button>
          {autoErro && <p className="mt-2 text-[13px] text-red-600">{autoErro}</p>}
        </div>
      )}

      {/* Logado free sem crédito — gera com marca OU compra limpo */}
      {user && !hasCleanAccess && (
        <div className="mb-6 border border-line bg-bg-alt px-5 py-4">
          <p className="text-[14px] font-bold text-ink">Você está logado</p>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-soft">
            Pode gerar automático com os meses do painel (sai com marca) ou comprar 1 PDF limpo
            por {AVULSO_COMPROVANTE.priceDisplay}.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={gerarAuto}
              disabled={autoState === 'loading'}
              className="inline-flex h-11 items-center justify-center gap-2 border border-line bg-white px-4 text-[13.5px] font-bold text-ink hover:border-brand disabled:opacity-60"
            >
              {autoState === 'loading' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Gerar automático (com marca)
            </button>
            <AvulsoComprovanteButton />
          </div>
          {autoErro && <p className="mt-2 text-[13px] text-red-600">{autoErro}</p>}
        </div>
      )}

      {/* Anônimo: oferta limpa visível desde o início — compra SEM cadastro */}
      {!user && !guestPaid && (
        <div className="mb-6 flex flex-col gap-3 border border-line bg-bg-alt px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[14px] font-bold text-ink">Precisa da versão limpa pro banco?</p>
            <p className="mt-0.5 text-[13px] text-ink-soft">
              1 PDF sem marca por {AVULSO_COMPROVANTE.priceDisplay}, sem cadastro. Ou gere grátis com
              marca abaixo.
            </p>
          </div>
          {guestBuyBtn}
        </div>
      )}

      {/* Identificação */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelCls}>Seu nome completo</label>
          <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Como está no CNPJ" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>CNPJ (opcional)</label>
          <input value={cnpj} onChange={(e) => setCnpj(e.target.value)} placeholder="00.000.000/0001-00" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Tipo de MEI (opcional)</label>
          <select value={tipoMEI} onChange={(e) => setTipoMEI(e.target.value)} className={inputCls}>
            <option value="">Não informar</option>
            <option value="Comércio">Comércio</option>
            <option value="Serviços">Serviços</option>
            <option value="Misto">Comércio + Serviços</option>
          </select>
        </div>
      </div>

      {/* 12 meses */}
      <div className="mt-6">
        <div className="flex items-baseline justify-between">
          <label className={labelCls}>Faturamento de cada mês (últimos 12)</label>
          <span className="text-[12px] text-ink-faint">deixe zerado o que não teve</span>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {janela.map((j, i) => (
            <div key={`${j.ano}-${j.mes}`} className="flex items-center gap-2 rounded-lg border border-line bg-white px-2.5 py-1.5">
              <span className="w-16 shrink-0 text-[12px] font-medium text-ink-mute">{j.label}</span>
              <div className="flex flex-1 items-center gap-1">
                <span className="text-[12px] text-ink-faint">R$</span>
                <input
                  inputMode="decimal"
                  value={valores[i]}
                  onChange={(e) => setValor(i, e.target.value)}
                  placeholder="0,00"
                  className="w-full bg-transparent text-[13px] font-mono text-ink outline-none"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Total + gerar */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
        <div>
          <div className="text-[12px] text-ink-faint">Faturamento dos 12 meses</div>
          <div className="font-mono text-[22px] font-bold text-ink">{fmt(total)}</div>
        </div>
        <button
          type="button"
          onClick={() => gerar()}
          disabled={state === 'loading'}
          className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-brand px-5 text-[14px] font-bold text-white hover:bg-brand-dark disabled:opacity-60"
        >
          {state === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {hasCleanAccess ? 'Gerar PDF limpo (manual)' : 'Gerar comprovante grátis'}
        </button>
      </div>

      {erro && <p className="mt-3 text-[13px] text-red-600">{erro}</p>}

      {/* Pós-grátis: oferta clara avulso vs Premium */}
      {state === 'done' && !hasCleanAccess && (
        <div className="mt-4 rounded-xl border-2 border-brand bg-brand-tint px-5 py-4.5">
          <p className="flex items-start gap-2 text-[15px] font-bold text-ink">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            Pronto! Baixou — mas essa versão saiu <span className="text-brand">com marca d’água</span>.
          </p>
          <p className="mt-1.5 flex items-start gap-2 text-[13.5px] leading-relaxed text-ink-soft">
            <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-ink-mute" />
            <span>
              <strong>Banco, aluguel e financiamento</strong> pedem a versão limpa. Com marca,
              costuma ser recusada.
            </span>
          </p>

          <div className="mt-3.5 flex items-stretch gap-3 rounded-lg border border-line bg-white/60 p-3">
            <MiniDoc watermark label="Grátis · com marca" tone="free" />
            <div className="flex items-center text-ink-faint">
              <ArrowRight className="h-4 w-4" />
            </div>
            <MiniDoc label="Limpo · sem marca" tone="premium" />
          </div>

          <ul className="mt-3.5 space-y-1.5">
            {[
              `1 PDF limpo agora — ${AVULSO_COMPROVANTE.priceDisplay}`,
              'Ou Premium: limpos ilimitados todo mês',
            ].map((b) => (
              <li key={b} className="flex items-start gap-2 text-[13px] text-ink-soft">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            {user ? <AvulsoComprovanteButton /> : guestBuyBtn}
            {user ? (
              <Link
                href="/premium"
                className="inline-flex items-center gap-1.5 rounded-lg border border-brand bg-white px-4 py-2.5 text-[13px] font-bold text-brand hover:bg-brand-tint"
              >
                <Sparkles className="h-4 w-4" /> Ou Premium {PLANS.PREMIUM.priceDisplay}/mês
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => openSignup('comprovante_limpo')}
                className="inline-flex items-center gap-1.5 rounded-lg border border-brand bg-white px-4 py-2.5 text-[13px] font-bold text-brand hover:bg-brand-tint"
              >
                <Sparkles className="h-4 w-4" /> Ou Premium {PLANS.PREMIUM.priceDisplay}/mês
              </button>
            )}
          </div>
          <p className="mt-2 text-[11.5px] text-ink-faint">
            Avulso = 1 PDF. Premium = ilimitado + painel. Sem fidelidade.
          </p>
        </div>
      )}

      {state === 'done' && hasCleanAccess && (
        <p className="mt-4 flex items-center gap-2 text-[14px] font-semibold text-brand">
          <Check className="h-4 w-4" /> PDF limpo baixado.
        </p>
      )}

      <p className="mt-4 text-[11.5px] leading-relaxed text-ink-faint">
        O comprovante é uma declaração do próprio titular, gerada a partir dos valores que você informa. Não
        substitui a escrituração contábil oficial nem a DASN-SIMEI.
      </p>
    </div>
  )
}
